import React, { createContext, useState, useContext, useCallback } from 'react';
// CORRECTION DU CHEMIN D'IMPORTATION ET DES NOMS DE FONCTIONS
import { apiCreateMarketAnalysis, apiGetMarketData, getToken, apiLogout } from '../api/api'; // Assurez-vous que le chemin est correct
import { apiMarketAnalysisAI } from '../api/api';

// Crée le contexte. La valeur par défaut est null.
const MarketAnalysisContext = createContext(null);

/**
 * Fournit l'état et les fonctions pour l'analyse de marché à tous les composants enfants.
 */
export const MarketAnalysisProvider = ({ children }) => {
    // États pour gérer l'entrée utilisateur et les résultats
    const [searchTerm, setSearchTerm] = useState('');
    const [analysisType, setAnalysisType] = useState('both');
    const [chartData, setChartData] = useState([]);
    const [analysisResult, setAnalysisResult] = useState('');

    // États pour gérer le chargement et les erreurs
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    /**
     * Fonction asynchrone pour déclencher l'analyse de marché et la récupération des données.
     * Utilise useCallback pour mémoriser la fonction et éviter des re-créations inutiles.
     * @param {string} term - Le symbole ou sujet à analyser.
     * @param {'both'|'technical'|'fundamental'} type - Le type d'analyse.
     */
    const analyzeMarket = useCallback(async (term, type) => {
        setLoading(true); // Début du chargement
        setError('');      // Réinitialise les erreurs précédentes
        setChartData([]);  // Réinitialise les données du graphique
        setAnalysisResult(''); // Réinitialise les résultats de l'analyse

        const token = getToken(); // Utilise la fonction getToken() de votre api.js
        if (!token) {
            setError('Veuillez vous connecter pour effectuer une analyse.');
            setLoading(false);
            apiLogout(); // S'assure que l'utilisateur est déconnecté si le jeton est manquant
            return;
        }

        try {
            // Exécute les deux appels API en parallèle pour améliorer les performances
            // Utilise les nouvelles fonctions apiMarketAnalysisAI et apiGetMarketData
            const [analysisRes, marketDataRes] = await Promise.all([
                apiMarketAnalysisAI(`Analyze ${term}`, term, type, token), // Le prompt est maintenant un message
                apiGetMarketData(term, 'full') // 'full' est une option par défaut, ajustez si nécessaire
            ]);

            // Vérifie les erreurs dans les réponses des API
            if (analysisRes.error) {
                throw new Error(analysisRes.error);
            }
            if (marketDataRes.error) {
                // Si les données de marché ne sont pas trouvées, on peut ignorer l'erreur pour ne pas bloquer l'analyse
                // ou afficher un message spécifique. Ici, on affiche l'erreur si elle existe.
                console.warn("Erreur lors de la récupération des données de marché:", marketDataRes.error);
                // Si vous voulez ignorer le graphique en cas d'erreur de données :
                // setChartData([]);
                // Sinon, propager l'erreur :
                throw new Error(marketDataRes.error);
            }

            setAnalysisResult(analysisRes.analysis_result || analysisRes.message || "Analyse complétée mais aucun résultat détaillé fourni.");
            // Assurez-vous que marketDataRes contient bien un tableau pour le graphique
            setChartData(marketDataRes.chart_data || []); // Assurez-vous que votre API renvoie les données sous `chart_data`

        } catch (err) {
            console.error('Erreur lors de l\'analyse du marché:', err);
            // Affiche un message d'erreur convivial à l'utilisateur
            setError(err.message || "Une erreur inattendue est survenue lors de l'analyse.");
        } finally {
            setLoading(false); // Fin du chargement
        }
    }, []); // La dépendance est vide car les fonctions importées sont stables

    /**
     * Fonction pour réinitialiser les résultats affichés.
     */
    const clearResults = useCallback(() => {
        setChartData([]);
        setAnalysisResult('');
        setError('');
    }, []);

    // La valeur qui sera fournie par le contexte à ses consommateurs
    const contextValue = {
        searchTerm,
        setSearchTerm,
        analysisType,
        setAnalysisType,
        chartData,
        analysisResult,
        loading,
        error,
        analyzeMarket,
        clearResults
    };

    return (
        <MarketAnalysisContext.Provider value={contextValue}>
            {children}
        </MarketAnalysisContext.Provider>
    );
};

/**
 * Hook personnalisé pour utiliser facilement le contexte de l'analyse de marché.
 * Lance une erreur si le hook est utilisé en dehors du MarketAnalysisProvider.
 */
export const useMarketAnalysis = () => {
    const context = useContext(MarketAnalysisContext);
    if (!context) {
        throw new Error('useMarketAnalysis doit être utilisé au sein d\'un MarketAnalysisProvider');
    }
    return context;
};